import * as React from "react";
import { useMemo } from "react";

import clsx from "clsx";

import { cn } from "~/lib/utils";

// AIDEV-NOTE: Design system spec — TextField is a clean rectangular input:
// white surface, 1px brand-forest-200 border, 8px radius, 10px×14px padding,
// 14px Inter body text, forest-800 focus ring at 12% opacity.
// When a `placeholder` is provided we keep the legacy floating-label behavior
// (Safari mobile quirk: text-base prevents auto-zoom on focus). When no
// placeholder is provided the field renders without the floating label so
// callers can use external `<label>` elements (see admin/design-system route).
const baseInputClassname =
    "block w-full rounded-lg border border-brand-forest-200 bg-white px-3.5 py-2.5 text-base sm:text-sm text-brand-forest-800 placeholder:text-brand-forest-800/40 transition-shadow focus:outline-none focus:border-brand-forest-300 focus:shadow-[0_0_0_3px_rgba(27,67,50,0.12)] disabled:cursor-not-allowed disabled:opacity-50";

// AIDEV-NOTE: text-base (16px) prevents Safari mobile auto-zoom on focus for
// the floating-label variant where placeholder is the visible label.
const floatingInputClassname =
    "peer block w-full appearance-none rounded-lg border border-brand-forest-200 bg-white px-3.5 pb-2.5 pt-5 text-base text-brand-forest-800 transition-shadow focus:outline-none focus:border-brand-forest-300 focus:shadow-[0_0_0_3px_rgba(27,67,50,0.12)]";

const placeholderToId = (placeholder?: string): string => {
    if (!placeholder) return "";
    return placeholder
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
        .replace(/\s+/g, "-"); // Replace spaces with dashes
};

const Input = React.forwardRef<
    HTMLInputElement,
    React.ComponentProps<"input"> & {
        wrapperClassName?: string;
        /**
         * Render `placeholder` as a plain native placeholder instead of the
         * floating label. Use for compact inputs (h-8/h-9 overrides) where the
         * floating variant's pt-5 layout would overlap the value (EVO-2217).
         */
        plainPlaceholder?: boolean;
    }
>(({ className, type, wrapperClassName, plainPlaceholder, ...props }, ref) => {
    const hasFloatingLabel = Boolean(props.placeholder) && !plainPlaceholder;
    const id = useMemo(
        () => placeholderToId(props.placeholder),
        [props.placeholder],
    );

    if (!hasFloatingLabel) {
        return (
            <input
                type={type}
                ref={ref}
                {...props}
                className={cn(baseInputClassname, className)}
            />
        );
    }

    return (
        <div className={clsx("relative", wrapperClassName)}>
            <input
                type={type}
                ref={ref}
                {...props}
                id={id}
                className={cn(floatingInputClassname, className)}
                // empty placeholder to make room for the label.
                placeholder=""
            />
            <label
                htmlFor={id}
                className="text-brand-forest-800 peer-focus:text-brand-forest-700 absolute start-3.5 top-4 z-10 origin-[0] -translate-y-3 scale-75 text-sm duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-3 peer-focus:scale-75 rtl:peer-focus:left-auto rtl:peer-focus:translate-x-1/4"
            >
                {props.placeholder}
            </label>
        </div>
    );
});
Input.displayName = "Input";

export { Input };
